// Single safe error-normalization contract for the Business/CRM Web plane.
//
// Before this, components rendered raw backend/Axios error objects directly inside toast/banner JSX —
// a custom envelope { detail: { code, message } } or a FastAPI 422 detail array [{loc,msg,type}] — which
// throws React error #31 ("Objects are not valid as a React child") and, because the app had no error
// boundary, white-paged the whole shell.
//
// getErrorMessage() ALWAYS returns a display string. normalizeError() returns { message, code, status }
// for callers that also want a safe code / retry decision. Neither EVER returns the raw object — the
// structured detail is deliberately kept out of the rendered child (diagnostics stay in console/logs).

const STATUS_MESSAGE = {
  400: 'That request could not be processed. Please check your input and try again.',
  401: 'Your session needs to refresh. Please try again in a moment.',
  403: 'You do not have access to that.',
  404: 'We could not find what you were looking for.',
  409: 'That action conflicts with the current state. Please refresh and try again.',
  422: 'Some details are invalid. Please review and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'Something went wrong on our side. Please try again.',
  502: 'The service is temporarily unavailable. Please try again.',
  503: 'This feature is temporarily unavailable. Please try again shortly.',
  504: 'The request timed out. Please try again.',
};

// Turn a backend `detail` (string | {code,message} | array of {msg}) into a string, or null.
function fromDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d && typeof d === 'object' ? (d.msg || d.message || d.detail) : (typeof d === 'string' ? d : null)))
      .filter(Boolean);
    return msgs.length ? msgs.join('; ') : null;
  }
  if (typeof detail === 'object') return detail.message || detail.msg || (typeof detail.detail === 'string' ? detail.detail : null) || null;
  return null;
}

/**
 * Normalize any thrown value / error response into a SAFE display shape.
 * Accepts: string, Error, Axios error (err.response.data), a raw fetch-parsed body object,
 * a {code,message} object, a 422 validation array, or a network/timeout error.
 * @returns {{message: string, code: (string|null), status: (number|null)}}
 */
export function normalizeError(err, fallback = 'Something went wrong. Please try again.') {
  let message = null;
  let code = null;
  let status = null;
  try {
    if (err == null) {
      message = fallback;
    } else if (typeof err === 'string') {
      message = err;
    } else if (typeof err === 'object') {
      status = err.status || (err.response && err.response.status) || null;
      const data = (err.response && err.response.data != null) ? err.response.data : (err.data != null ? err.data : null);
      const detail = data && (data.detail != null ? data.detail : data);
      code =
        (data && typeof data === 'object' && data.code) ||
        (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) ||
        err.code ||
        null;
      message =
        fromDetail(data && data.detail) ||
        (data && typeof data === 'object' ? (data.message || data.msg) : null) ||
        (typeof data === 'string' ? data : null) ||
        (err.message && !/^request failed|network error/i.test(err.message) ? err.message : null);
      if (!message && err.message) {
        if (/timeout|ecconnaborted|ecconnreset/i.test(err.message)) message = STATUS_MESSAGE[504];
        else if (/network|failed to fetch|connection/i.test(err.message)) message = 'Connection problem. Check your network and try again.';
      }
      if (!message && status && STATUS_MESSAGE[status]) message = STATUS_MESSAGE[status];
    }
  } catch {
    /* fall through to fallback */
  }
  if (!message || typeof message !== 'string') {
    message = (status && STATUS_MESSAGE[status]) || fallback;
  }
  return { message, code: (typeof code === 'string' ? code : (status ? `HTTP_${status}` : null)), status: status || null };
}

/** Convenience: always returns a display string (never an object). Use in toast/banner children. */
export function getErrorMessage(err, fallback) {
  return normalizeError(err, fallback).message;
}

export default getErrorMessage;
